import { SevenNewsSections } from '@west-australian-newspapers/publication-types'
import { BaseSectionValues } from '.'
import { SocialLinkValues } from '../../../navigation'
import { colors } from '../colors'

export type SevenNewsSection = keyof SevenNewsSections

interface Colors {
    primaryColor: string
    secondaryColor?: string
}

interface DesktopNavColors extends Colors {
    primaryColor: string
    hoverColour: string
    textColor: string
    hoverTextColor: string
    boxShadow?: string
    backgroundColor?: string
    stickyText?: string
}

interface ColorsWithHover extends Colors {
    hoverColor: string
}

interface CardsColors {
    kicker: ColorsWithHover
    headline: ColorsWithHover
    list: ColorsWithHover
    timestamp: ColorsWithHover
}

interface LocationSelectorColors {
    headerBackground: string
    dropdownBackground: string
    hoverColor: string
    focusColor: string
    selectedColor: string
    textColor: string
}

interface FlyoutNavColors {
    primaryColour: string
    background: string
    mainSectionColour: string
    mainSectionTextColour: string
    mainSectionHoverColour: string
    mainSectionActiveColour: string
    mainSectionFocusColour: string
    hoverColour: string
    focusColour: string
    textColour: string
    scrollBarBase: string
    scrollBarThumb: string
    linkColour?: string
}

export interface SevenNewsSectionValues extends BaseSectionValues {
    focusColor: string
    flyoutNav: FlyoutNavColors
    footerNav: Colors
    desktopNav: DesktopNavColors
    locationSelector: LocationSelectorColors
    sectionHeader: { element: ColorsWithHover; text: ColorsWithHover }
    button: ColorsWithHover
    video: Colors & { playOverlay: string; focusColor: string }
    breadCrumb: ColorsWithHover
    cards: CardsColors
    tag: Colors
    socialLinks?: SocialLinkValues
    title: string
    link: Colors
}

export type SevenNewsSectionProps = {
    [section in SevenNewsSection]: SevenNewsSectionValues
}

export const sevenNewsSectionDefinitions: SevenNewsSectionProps = {
    default: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.red,
            background: colors.white,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.sevennews.red,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: 'rgba(182,9,32,0.03)',
            scrollBarThumb: colors.sevennews.darkRed,
            linkColour: colors.sevennews.flyoutLinkRed,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.white,
            hoverColour: colors.sevennews.red,
            textColor: colors.sevennews.charade,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
        },
        locationSelector: {
            headerBackground: colors.sevennews.red,
            dropdownBackground: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
    sunrise: {
        primaryColor: colors.sevennews.piper,
        focusColor: colors.sevennews.blazeOrange,
        flyoutNav: {
            primaryColour: colors.black,
            background: colors.black,
            hoverColour: colors.sevennews.piperDark,
            mainSectionColour: colors.sevennews.seashell,
            mainSectionTextColour: colors.black,
            mainSectionHoverColour: 'transparent',
            mainSectionActiveColour: colors.sevennews.stormGrey,
            mainSectionFocusColour: colors.black,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: colors.sevennews.chillOrange,
            scrollBarThumb: colors.sevennews.piperDark,
        },
        footerNav: {
            primaryColor: colors.sevennews.piper,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.piperDark,
            textColor: colors.white,
            hoverTextColor: colors.white,
            backgroundColor: colors.black,
        },
        locationSelector: {
            headerBackground: colors.sevennews.seashell,
            dropdownBackground: colors.sevennews.seashell,
            focusColor: colors.black,
            textColor: colors.sevennews.charade,
            hoverColor: colors.white,
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.piper,
            },
        },
        button: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        video: {
            primaryColor: colors.sevennews.blazeOrange,
            playOverlay: colors.sevennews.piper,
            focusColor: colors.sevennews.blazeOrange,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.piper,
            },
            timestamp: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
        },
        tag: {
            primaryColor: colors.sevennews.piper,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/Sunrise/',
            twitter: 'https://twitter.com/sunriseon7',
            instagram: 'https://www.instagram.com/sunriseon7/',
            // youtube: 'https://www.youtube.com/user/SunriseOn7',
            email: 'mailto:sunrise@seven.com.au',
        },
        title: 'Sunrise',
    },
    'whats-for-dinner': {
        primaryColor: colors.sevennews.piper,
        focusColor: colors.sevennews.blazeOrange,
        flyoutNav: {
            primaryColour: colors.black,
            background: colors.black,
            hoverColour: colors.sevennews.piperDark,
            mainSectionColour: colors.sevennews.seashell,
            mainSectionTextColour: colors.black,
            mainSectionHoverColour: 'transparent',
            mainSectionActiveColour: colors.sevennews.stormGrey,
            mainSectionFocusColour: colors.black,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: colors.sevennews.chillOrange,
            scrollBarThumb: colors.sevennews.piperDark,
        },
        footerNav: {
            primaryColor: colors.sevennews.piper,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.piperDark,
            textColor: colors.white,
            hoverTextColor: colors.white,
            backgroundColor: colors.black,
        },
        locationSelector: {
            headerBackground: colors.sevennews.seashell,
            dropdownBackground: colors.sevennews.seashell,
            focusColor: colors.black,
            textColor: colors.sevennews.charade,
            hoverColor: colors.white,
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.piper,
            },
        },
        button: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        video: {
            primaryColor: colors.sevennews.blazeOrange,
            playOverlay: colors.sevennews.piper,
            focusColor: colors.sevennews.blazeOrange,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.piper,
            hoverColor: colors.sevennews.blazeOrange,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.piper,
            },
            timestamp: {
                primaryColor: colors.sevennews.piper,
                hoverColor: colors.sevennews.blazeOrange,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.piper,
            },
        },
        tag: {
            primaryColor: colors.sevennews.piper,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/Sunrise/',
            twitter: 'https://twitter.com/sunriseon7',
            instagram: 'https://www.instagram.com/sunriseon7/',
            // youtube: 'https://www.youtube.com/user/SunriseOn7',
            email: 'mailto:sunrise@seven.com.au',
        },
        title: 'Sunrise',
    },
    'the-morning-show': {
        primaryColor: colors.sevennews.endeavour,
        focusColor: colors.sevennews.azureRadiance,
        flyoutNav: {
            primaryColour: colors.black,
            background: colors.black,
            hoverColour: colors.sevennews.endeavourDark,
            mainSectionColour: colors.sevennews.seashell,
            mainSectionTextColour: colors.black,
            mainSectionHoverColour: 'transparent',
            mainSectionActiveColour: colors.sevennews.stormGrey,
            mainSectionFocusColour: colors.black,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: colors.sevennews.chillEndeavour,
            scrollBarThumb: colors.sevennews.endeavourDark,
        },
        footerNav: {
            primaryColor: colors.sevennews.endeavour,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.endeavourDark,
            textColor: colors.white,
            hoverTextColor: colors.white,
            backgroundColor: colors.black,
        },
        locationSelector: {
            headerBackground: colors.sevennews.seashell,
            dropdownBackground: colors.sevennews.seashell,
            focusColor: colors.black,
            textColor: colors.sevennews.charade,
            hoverColor: colors.white,
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.azureRadiance,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.azureRadiance,
            },
        },
        button: {
            primaryColor: colors.sevennews.endeavour,
            hoverColor: colors.sevennews.azureRadiance,
        },
        video: {
            primaryColor: colors.sevennews.azureRadiance,
            playOverlay: colors.sevennews.endeavour,
            focusColor: colors.sevennews.azureRadiance,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.endeavour,
            hoverColor: colors.sevennews.azureRadiance,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.endeavour,
            },
            timestamp: {
                primaryColor: colors.sevennews.endeavour,
                hoverColor: colors.sevennews.endeavour,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.azureRadiance,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.azureRadiance,
            },
        },
        tag: {
            primaryColor: colors.sevennews.endeavour,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            facebook: 'https://www.facebook.com/morningshowon7/',
            twitter: 'https://twitter.com/morningshowon7',
            instagram: 'https://www.instagram.com/morningshowon7/',
            // youtube: 'https://www.youtube.com/user/themorningshowaus',
            email: 'mailto:themorningshow@seven.com.au',
        },
        title: 'The Morning Show',
    },
    spotlight: {
        primaryColor: colors.sevennews.pickledBluewood,
        focusColor: colors.sevennews.kashmirBlue,
        flyoutNav: {
            primaryColour: colors.black,
            background: colors.black,
            hoverColour: colors.sevennews.pickledBluewoodDark,
            focusColour: colors.white,
            textColour: colors.white,
            mainSectionColour: colors.sevennews.seashell,
            mainSectionTextColour: colors.black,
            mainSectionHoverColour: 'transparent',
            mainSectionActiveColour: colors.sevennews.stormGrey,
            mainSectionFocusColour: colors.black,
            scrollBarBase: colors.sevennews.chillPickledBluewood,
            scrollBarThumb: colors.sevennews.pickledBluewoodDark,
        },
        footerNav: {
            primaryColor: colors.sevennews.pickledBluewood,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.pickledBluewoodDark,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.sevennews.seashell,
            dropdownBackground: colors.sevennews.seashell,
            focusColor: colors.black,
            textColor: colors.sevennews.charade,
            hoverColor: colors.white,
            selectedColor: colors.sevennews.stormGrey,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.pickledBluewood,
                hoverColor: colors.sevennews.charade,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.kashmirBlue,
            },
        },
        button: {
            primaryColor: colors.sevennews.pickledBluewood,
            hoverColor: colors.sevennews.charade,
        },
        video: {
            primaryColor: colors.sevennews.kashmirBlue,
            playOverlay: colors.sevennews.pickledBluewood,
            focusColor: colors.sevennews.kashmirBlue,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.pickledBluewood,
            hoverColor: colors.sevennews.charade,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.kashmirBlue,
                hoverColor: colors.sevennews.kashmirBlue,
            },
            timestamp: {
                primaryColor: colors.sevennews.stormGrey,
                hoverColor: colors.sevennews.stormGrey,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.kashmirBlue,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.kashmirBlue,
            },
        },

        tag: {
            primaryColor: colors.sevennews.pickledBluewood,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
    'big-brother-australia': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Big Brother Australia',
    },
    'sas-australia': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'SAS Australia',
    },
    entertainment: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.white30,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.white30,
            mainSectionActiveColour: colors.white30,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.white30,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Entertainment',
    },
    lifestyle: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.sevennews.lifestyleFlyoutPurple,
            hoverColour: colors.white30,
            mainSectionColour: colors.sevennews.lifestyleFlyoutPurple,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.white30,
            mainSectionActiveColour: colors.white30,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.sevennews.lifestylePurple,
            hoverColour: colors.white30,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.sevennews.lifestylePurple,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.white30,
                hoverColor: colors.white30,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.white30,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Lifestyle',
    },
    'dancing-with-the-stars': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    'the-voice-australia': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    'home-and-away': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },

    'my-kitchen-rules': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    'heartbreak-island': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    agt: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    'australian-idol': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    wcs: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    sport: {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.red,
            background: colors.white,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.sevennews.red,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: 'rgba(182,9,32,0.03)',
            scrollBarThumb: colors.sevennews.darkRed,
            linkColour: colors.sevennews.flyoutLinkRed,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.white,
            hoverColour: colors.sevennews.red,
            textColor: colors.sevennews.charade,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
        },
        locationSelector: {
            headerBackground: colors.sevennews.red,
            dropdownBackground: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
    'comm-games': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.red,
            background: colors.white,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.sevennews.red,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: 'rgba(182,9,32,0.03)',
            scrollBarThumb: colors.sevennews.darkRed,
            linkColour: colors.sevennews.flyoutLinkRed,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.white,
            hoverColour: colors.sevennews.red,
            textColor: colors.sevennews.charade,
            stickyText: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
        },
        locationSelector: {
            headerBackground: colors.sevennews.red,
            dropdownBackground: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: '7NEWS',
    },
    'farmer-wants-a-wife': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
    'kitchen-nightmares-australia': {
        primaryColor: colors.sevennews.red,
        focusColor: colors.sevennews.charade,
        flyoutNav: {
            primaryColour: colors.sevennews.azureRadiance,
            background: colors.black,
            hoverColour: colors.sevennews.darkRed,
            mainSectionColour: colors.black,
            mainSectionTextColour: colors.white,
            mainSectionHoverColour: colors.sevennews.darkRed,
            mainSectionActiveColour: colors.sevennews.darkRed,
            mainSectionFocusColour: colors.white,
            focusColour: colors.white,
            textColour: colors.white,
            scrollBarBase: '#838383',
            scrollBarThumb: colors.white50,
        },
        footerNav: {
            primaryColor: colors.sevennews.red,
        },
        desktopNav: {
            primaryColor: colors.black,
            hoverColour: colors.sevennews.red,
            textColor: colors.white,
            hoverTextColor: colors.white,
            boxShadow: `0px 10px 16px ${colors.sevennews.boxShadow}`,
            backgroundColor: colors.black,
            stickyText: colors.white,
        },
        locationSelector: {
            headerBackground: colors.black,
            dropdownBackground: colors.black,
            hoverColor: colors.sevennews.darkRed,
            textColor: colors.white,
            focusColor: colors.white,
            selectedColor: colors.sevennews.red,
        },
        sectionHeader: {
            element: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            text: {
                primaryColor: colors.black,
                hoverColor: colors.sevennews.red,
            },
        },
        button: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        video: {
            primaryColor: colors.sevennews.red,
            playOverlay: colors.sevennews.red,
            focusColor: colors.sevennews.darkRed,
        },
        breadCrumb: {
            primaryColor: colors.sevennews.red,
            hoverColor: colors.sevennews.darkRed,
        },
        cards: {
            kicker: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            timestamp: {
                primaryColor: colors.sevennews.red,
                hoverColor: colors.sevennews.red,
            },
            headline: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
            list: {
                primaryColor: colors.sevennews.charade,
                hoverColor: colors.sevennews.red,
            },
        },
        tag: {
            primaryColor: colors.sevennews.red,
        },
        link: {
            primaryColor: colors.sevennews.red,
        },
        socialLinks: {
            twitter: 'https://twitter.com/7NewsAustralia/',
            facebook: 'https://facebook.com/7NewsAustralia/',
            instagram: 'https://instagram.com/7newsaustralia',
            email: 'mailto:7ndtips@seven.com.au',
        },
        title: 'Dancing With The Stars',
    },
}
